import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {scrollerFor} from './useSectionObserver';

/**
 * Difiere el montaje de un panel pesado hasta que el scroll se le acerca.
 *
 * El Recorrido junta en un solo DOM lo que antes eran cuatro pantallas: fuentes, cola de hechos,
 * resumen, bitácora y notas, cada una con su fetch. Montarlas todas de entrada es pagar las cinco
 * pantallas para ver la primera.
 *
 * Tres reglas:
 *
 * 1. **Nunca desmonta.** Se monta una vez y se queda: un panel que se desmonta pierde su estado
 *    (filtros, paginado, lo que el usuario tipeó) y devuelve su alto al placeholder, lo que mueve
 *    el scroll bajo el dedo. Ahorrar memoria no vale eso.
 *
 * 2. **Montar algo ARRIBA tuyo no puede patearte la vista.** Si el panel que se monta empieza por
 *    encima del scroll actual, crece hacia arriba y todo lo de abajo —lo que estás mirando— baja
 *    esa misma cantidad. Se compensa sumándosela al `scrollTop` en el mismo frame, antes del paint.
 *
 * 3. **`eager` gana siempre.** Lo usan el acto al que apunta el hash (tiene que estar montado antes
 *    de que el scroll llegue) y la Bitácora con deep-link, que lee la query en su constructor.
 *
 * El placeholder reserva alto para que la barra de scroll no mienta y para que el observer tenga
 * una caja que mirar (un div de alto 0 nunca "se acerca" a nada).
 */

// Se monta ANTES de entrar en pantalla: para el usuario el panel ya está cuando llega. 600px es
// poco menos de una pantalla de alto en un portátil, o sea que se gana el scroll de un tirón.
const NEAR_MARGIN = '600px 0px 600px 0px';

function measure(host, scroller) {
    const rect = host.getBoundingClientRect();
    if (!scroller) return {scroller: null, top: 0, height: rect.height};
    const box = scroller.getBoundingClientRect();
    // Posición de la sección dentro del CONTENIDO del scroller, no de la pantalla: es lo único
    // comparable contra `scrollTop`.
    return {scroller, top: rect.top - box.top + scroller.scrollTop, height: rect.height};
}

export function LazySection({minHeight = 320, eager = false, children}) {
    const hostRef = useRef(null);
    const pendingRef = useRef(null);
    // Sin IntersectionObserver no hay forma de saber cuándo se acerca: se monta todo, que es
    // exactamente el comportamiento previo a este componente (jsdom entra por acá).
    const hasIO = typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
    const [mounted, setMounted] = useState(false);
    const shouldMount = mounted || eager || !hasIO;

    // Latch: una vez montado se queda, aunque `eager` vuelva a false (el hash se movió a otro acto).
    useEffect(() => {
        if (shouldMount && !mounted) setMounted(true);
    }, [shouldMount, mounted]);

    useEffect(() => {
        if (shouldMount) return undefined;
        const host = hostRef.current;
        if (!host) return undefined;
        // El mismo `scrollerFor` que usa el observer de secciones: con una copia inline, cambiar la
        // clase del contenedor de scroll rompía uno de los dos en silencio.
        const scroller = scrollerFor(host);

        const observer = new window.IntersectionObserver((entries) => {
            if (!entries.some(entry => entry.isIntersecting)) return;
            observer.disconnect();
            // Se mide con el placeholder todavía en pantalla: después del commit ya es tarde.
            pendingRef.current = measure(host, scroller);
            setMounted(true);
        }, {root: scroller || null, rootMargin: NEAR_MARGIN, threshold: 0});

        observer.observe(host);
        return () => observer.disconnect();
    }, [shouldMount]);

    useLayoutEffect(() => {
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (!shouldMount || !pending || !pending.scroller) return;
        const host = hostRef.current;
        if (!host) return;
        const grown = host.getBoundingClientRect().height - pending.height;
        if (!grown) return;
        // Se compensa SÓLO lo que creció por encima del borde superior de lo que se ve.
        //
        // El crecimiento se inserta ADENTRO del panel, así que "el panel está arriba del scroll" no
        // alcanza como condición: con el panel a caballo del borde (`top < scrollTop < bottom`),
        // arriba del viewport quedó apenas un pedazo del placeholder y sumar el `grown` entero
        // empuja la página de más — pasando de largo el panel que se acaba de revelar. Pasa al
        // scrollear rápido hacia arriba, cuando el callback del observer llega uno o más frames
        // tarde.
        //
        // El reemplazo es el mismo cálculo que hace el scroll anchoring del navegador: si el
        // contenido del panel se estira parejo, el punto que estaba en el borde superior se
        // desplaza en proporción a cuánto del panel viejo quedaba por encima de él. Los dos
        // extremos siguen dando lo de antes — panel entero arriba, `grown` completo; panel entero
        // abajo, nada— y el medio deja de saltar.
        const above = pending.scroller.scrollTop - pending.top;
        if (above <= 0) return;
        const ratio = pending.height > 0 ? Math.min(1, above / pending.height) : 1;
        pending.scroller.scrollTop += grown * ratio;
    }, [shouldMount]);

    return (
        <div className="iab-flow-lazy" ref={hostRef}>
            {shouldMount ? children : <div className="iab-flow-placeholder" style={{minHeight}}/>}
        </div>
    );
}

export default LazySection;
